import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Affiliation } from '@/types/profile';

import ModalShell from '@/components/modal-shell';
import { Button } from '@/components/ui/button';

export interface AffiliationUsage {
  id: string;
  title: string;
}

interface AffiliationRemovalDialogProps {
  affiliation: Affiliation;
  usage: AffiliationUsage[];
  /** Other active affiliations the collections could be reassigned to. */
  others: Affiliation[];
  onCancel: () => void;
  /** toAffiliationId = null → move affected collections to Unaffiliated. */
  onConfirm: (toAffiliationId: string | null) => void;
}

export default function AffiliationRemovalDialog({ affiliation, usage, others, onCancel, onConfirm }: AffiliationRemovalDialogProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'unaffiliate' | 'reassign'>(others.length > 0 ? 'reassign' : 'unaffiliate');
  const [toId, setToId] = useState<string>(others[0]?.id ?? '');

  return (
    <ModalShell title={t('profile.removal.title')} onClose={onCancel}>
      <p className='flex items-start gap-2 text-sm text-ws-text'>
        <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-ws-warning-fg' />
        {t('profile.removal.message', { institution: affiliation.institution, count: usage.length })}
      </p>

      <div className='rounded-lg border border-ws-border bg-ws-bg p-3'>
        <p className='mb-1.5 text-xs font-semibold uppercase tracking-wide text-ws-muted'>{t('profile.removal.affected')}</p>
        <ul className='space-y-0.5'>
          {usage.map((c) => <li key={c.id} className='truncate text-sm text-ws-text'>{c.title}</li>)}
        </ul>
      </div>

      <div className='flex flex-col gap-2'>
        {others.length > 0 && (
          <label className={`flex items-center gap-2.5 rounded-lg border p-3 ${mode === 'reassign' ? 'border-ws-primary' : 'border-ws-border'}`}>
            <input type='radio' name='mode' checked={mode === 'reassign'} onChange={() => setMode('reassign')} className='accent-ws-primary' />
            <span className='text-sm text-ws-text'>{t('profile.removal.reassign')}</span>
            <select
              aria-label={t('profile.removal.reassign')}
              value={toId}
              onChange={(e) => { setToId(e.target.value); setMode('reassign'); }}
              className='ml-auto rounded-md border border-ws-border bg-ws-control px-2 py-1 text-sm text-ws-text'
            >
              {others.map((a) => <option key={a.id} value={a.id}>{a.institution}</option>)}
            </select>
          </label>
        )}
        <label className={`flex items-center gap-2.5 rounded-lg border p-3 ${mode === 'unaffiliate' ? 'border-ws-primary' : 'border-ws-border'}`}>
          <input type='radio' name='mode' checked={mode === 'unaffiliate'} onChange={() => setMode('unaffiliate')} className='accent-ws-primary' />
          <span className='text-sm text-ws-text'>{t('profile.removal.unaffiliate')}</span>
        </label>
      </div>

      <div className='flex items-center justify-between gap-3'>
        <Button size='lg' onClick={onCancel}>{t('common.cancel')}</Button>
        <button onClick={() => onConfirm(mode === 'reassign' ? toId : null)} className='rounded-lg bg-ws-primary px-5 py-2 text-sm font-medium text-white hover:opacity-90'>{t('profile.removal.confirm')}</button>
      </div>
    </ModalShell>
  );
}
